import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Offcanvas } from 'react-bootstrap'
import Select from 'react-select'

import { GET, PUT } from 'helpers/api'
import { formatMoneyInput } from 'helpers/helpers'

import { PAYMENT_TYPES, currencyLabel, expenseAccountName, expenseDetails, formatAmount, parseAmountInput } from './financeUtils'

// one row per ExpenseResponse.amountDetails item; payment types, amounts and the note can be changed
function getForm(item) {
	return {
		// rows removed by an earlier update stay in amountDetails with amount 0 and are not shown
		payments: expenseDetails(item)
			.filter(detail => Number(detail.amount) > 0)
			.map(detail => ({ id: detail.id, paymentType: detail.paymentType.key, amount: String(Number(detail.amount)) })),
		note: item.note || '',
	}
}

// UpdateExpenseRequest.items: a row keeps its id when its payment type is changed,
// an added row reuses the id of an unused row with the same payment type (null = inserted),
// existing rows that were removed from the form are sent with amount 0
function getRequestItems(item, form) {
	const details = expenseDetails(item)
	const items = form.payments.map(payment => {
		const paymentType = PAYMENT_TYPES.find(type => type.key === payment.paymentType)
		const unused = payment.id ? null : details.find(detail =>
			detail.paymentType === paymentType && !form.payments.some(other => other.id === detail.id)
		)
		return { id: payment.id || (unused ? unused.id : null), paymentTypeId: paymentType.id, amount: Number(payment.amount), note: form.note }
	})
	details
		.filter(detail => Number(detail.amount) > 0 && !items.some(row => row.id === detail.id))
		.forEach(detail => items.push({ id: detail.id, paymentTypeId: detail.paymentType.id, amount: 0, note: form.note }))
	return items
}

function LockedField({ children }) {
	return (
		<div className="form-control fo-locked-field">
			<span className="text-truncate">{children}</span>
			<i className="uil uil-lock text-muted"></i>
		</div>
	)
}

function EditExpense({ show, item, onHide, onSaved }) {
	const { t } = useTranslation()

	const [form, setForm] = useState({ payments: [], note: '' })
	const [balance, setBalance] = useState(null)
	const [saving, setSaving] = useState(false)

	const isSafe = item?.sourceType === 'SAFE'
	const paymentTypeOptions = PAYMENT_TYPES.map(type => ({ value: type.key, label: t(type.i18n) }))
	const currency = item ? currencyLabel(item.currencyId, item.currencyName) : ''
	const total = form.payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)
	const invalid = form.payments.length === 0 || form.payments.some(payment => !(Number(payment.amount) > 0))

	// cash/terminal balance of the expense's POS safe, or of its cashbox if the shift is still open
	async function getBalance(isActive) {
		if (isSafe) {
			const response = await GET('/services/web/api/expense/safe-with-balance', { posId: item.posId, currencyId: item.currencyId })
			if (!isActive() || !Array.isArray(response)) return
			setBalance(response.find(safe => safe.posId === item.posId) || null)
			return
		}
		const response = await GET('/services/web/api/expense/open-cashbox-with-balance', { currencyId: item.currencyId })
		if (!isActive() || !Array.isArray(response)) return
		setBalance(response.find(cashbox => item.cashboxId ? cashbox.cashboxId === item.cashboxId : cashbox.cashboxName === item.cashboxName) || null)
	}

	function changePayment(index, key, value) {
		setForm({ ...form, payments: form.payments.map((payment, i) => i === index ? { ...payment, [key]: value } : payment) })
	}

	function addPayment() {
		const unused = PAYMENT_TYPES.find(type => !form.payments.some(payment => payment.paymentType === type.key))
		setForm({ ...form, payments: [...form.payments, { id: null, paymentType: unused.key, amount: '' }] })
	}

	function removePayment(index) {
		setForm({ ...form, payments: form.payments.filter((payment, i) => i !== index) })
	}

	async function save() {
		// shiftId is null for SAFE
		const request = {
			sourceType: item.sourceType,
			shiftId: item.shiftId,
			items: getRequestItems(item, form),
		}

		setSaving(true)
		try {
			const response = await PUT('/services/web/api/expense-update', request, { loader: true })
			if (response) {
				onSaved()
			}
		} finally {
			setSaving(false)
		}
	}

	useEffect(() => {
		if (!show || !item) return
		let active = true
		setForm(getForm(item))
		getBalance(() => active)
		return () => { active = false }
	}, [show, item]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Offcanvas show={show} placement="end" className="fo-offcanvas" backdrop="static"
			onHide={onHide} onExited={() => setBalance(null)}>
			{item &&
				<>
					<Offcanvas.Header closeButton>
						<Offcanvas.Title>{t('FO_EDITING')} · {t('FO_EXPENSE')}</Offcanvas.Title>
					</Offcanvas.Header>
					<Offcanvas.Body>
						<div className="form-group">
							<label>{t('OPERATION_TYPE')}</label>
							<div className="fo-operation-type active">
								<span className="fo-operation-type-icon">
									<i className="uil uil-receipt"></i>
								</span>
								{t('FO_OTHER_EXPENSE')}
								<input type="radio" className="form-check-input" tabIndex="-1" checked readOnly />
							</div>
						</div>

						<div className="form-group">
							<label>{t('TYPE')}</label>
							<LockedField>{item.expenseHelperName}</LockedField>
						</div>

						<div className="form-group">
							<label>{t('ACCOUNT')}</label>
							<LockedField>
								{expenseAccountName(item, t)}
								{isSafe && item.posName &&
									<span className="text-muted"> ({item.posName})</span>
								}
							</LockedField>
							{balance &&
								<div className="text-muted text-sm mt-1">
									{PAYMENT_TYPES
										.map(paymentType => `${t(paymentType.i18n)} ${formatAmount(balance[paymentType.key], currency)}`)
										.join(' · ')}
								</div>
							}
						</div>

						<div className="form-group">
							<label>{t('AMOUNT')}</label>
							{form.payments.map((payment, index) => (
								<div key={payment.paymentType} className="fo-payment-row d-flex gap-2 mb-2">
									<div className="fo-payment-type">
										<Select
											options={paymentTypeOptions.filter(option =>
												option.value === payment.paymentType || !form.payments.some(other => other.paymentType === option.value)
											)}
											value={paymentTypeOptions.find(option => option.value === payment.paymentType)}
											onChange={(option) => changePayment(index, 'paymentType', option.value)}
											isSearchable={false}
										/>
									</div>
									<div className="input-group">
										<input type="text" inputMode="decimal" className="form-control" placeholder="0"
											value={formatMoneyInput(payment.amount)}
											onChange={(e) => changePayment(index, 'amount', parseAmountInput(e.target.value))} />
										<span className="input-group-text fo-currency">{currency}</span>
									</div>
									{form.payments.length > 1 &&
										<button type="button" className="btn btn-outline-danger" title={t('DELETE')} onClick={() => removePayment(index)}>
											<i className="uil uil-trash-alt"></i>
										</button>
									}
								</div>
							))}
							<div className="d-flex align-items-center">
								{form.payments.length < PAYMENT_TYPES.length &&
									<button type="button" className="btn btn-link text-primary text-decoration-none p-0" onClick={addPayment}>
										<i className="uil uil-plus me-1"></i>
										{t('ADD')}
									</button>
								}
								<div className="ms-auto">
									<b>{t('GRAND_TOTAL')}:</b> {formatAmount(total, currency)}
								</div>
							</div>
						</div>

						<div className="form-group">
							<label>{t('COMMENT')}</label>
							<textarea className="form-control" rows="3" placeholder={t('NOT_NECESSARY')}
								value={form.note}
								onChange={(e) => setForm({ ...form, note: e.target.value })} />
						</div>
					</Offcanvas.Body>

					<div className="fo-offcanvas-footer">
						<button type="button" className="btn btn-outline-warning ms-auto" onClick={onHide}>{t('CANCEL')}</button>
						<button type="button" className="btn btn-primary" disabled={invalid || saving} onClick={save}>{t('SAVE')}</button>
					</div>
				</>
			}
		</Offcanvas>
	)
}

export default EditExpense
